import { useEffect, useState } from "react";
import { Check, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { getPlans, startCheckout, cancelPlan, formatApiError, payfastCheckout, goToCheckout } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ProviderPicker, pickDefaultProvider } from "./ProviderPicker";

const FREE = ["1 AI per question (your pick)", "Private chat history", "Share answers"];
const PRO = ["ChatGPT, Claude & Gemini together", "One combined best answer", "AI agreement score", "AIs debate when they disagree", "Choose which AI combines"];

const Perks = ({ items, accent }) => (
  <ul className="space-y-2 mt-4">
    {items.map((p) => (
      <li key={p} className="flex items-start gap-2 text-sm text-slate-300">
        <Check size={14} className="mt-1 shrink-0" style={{ color: accent }} /> {p}
      </li>
    ))}
  </ul>
);

export const PlanDialog = ({ open, onOpenChange }) => {
  const { user, refreshUser } = useAuth();
  const [plans, setPlans] = useState(null);
  const [busy, setBusy] = useState(false);
  const [provider, setProvider] = useState("paystack");
  const [interval, setInterval] = useState("month");
  useEffect(() => { if (open && user?.plan === "pro") setInterval(user.plan_interval === "year" ? "year" : "month"); }, [open, user]);
  const isPro = user?.plan === "pro";
  const trialEligible = !isPro && !user?.trial_used;
  const trialEnds = user?.trial_ends_at ? new Date(user.trial_ends_at) : null;
  const onTrial = isPro && trialEnds && trialEnds > new Date();
  const providerReady = !!plans?.providers?.[provider];

  useEffect(() => {
    if (open) getPlans().then((p) => { setPlans(p); setProvider(pickDefaultProvider(p.providers)); }).catch(() => {});
  }, [open]);

  const upgrade = async () => {
    setBusy(true);
    try {
      goToCheckout(provider === "payfast" ? await payfastCheckout("pro", undefined, interval) : await startCheckout(interval));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  const liveTest = async () => {
    setBusy(true);
    try {
      goToCheckout(await payfastCheckout("live_test"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      await cancelPlan();
      await refreshUser();
      toast.success(onTrial ? "Your free trial has been cancelled" : "Your Pro plan has been cancelled");
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setBusy(false);
  };

  const symbols = { ZAR: "R", USD: "$" };
  const symbol = plans ? symbols[plans.pro.currency] || plans.pro.currency + " " : "R";
  const fmt = (n) => `${symbol}${Number(n).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
  const yearly = interval === "year";
  const amount = plans ? (yearly ? plans.pro.yearly_price : plans.pro.price) : yearly ? 1750 : 190;
  const per = yearly ? "year" : "month";
  const price = fmt(amount);
  const freePrice = `${symbol}0`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="plan-dialog" className="max-w-2xl bg-[#0E131D] border-white/[0.08] text-slate-200 p-8 font-['Plus_Jakarta_Sans',sans-serif]">
        <DialogTitle className="font-display text-2xl font-bold text-white tracking-tight">
          {onTrial ? "You're on your WHAT? Pro free trial" : isPro ? "You're on WHAT? Pro" : trialEligible ? "Try WHAT? Pro free for 14 days" : "Unlock every AI with WHAT? Pro"}
        </DialogTitle>
        <DialogDescription className="text-slate-400">
          {onTrial
            ? `Your trial ends on ${trialEnds.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}. After that, ${price}/${per} unless you cancel.`
            : isPro ? "All three AIs combined into one best answer." : "Free gets you one AI. Pro asks all three, lets them debate when they disagree, and merges the best of them."}
        </DialogDescription>
        {!isPro && (
          <div data-testid="billing-interval" role="radiogroup" aria-label="Billing period" className="what-mode-switch self-start mt-2">
            {[["month", "Monthly"], ["year", "Yearly"]].map(([v, label]) => (
              <button key={v} type="button" role="radio" aria-checked={interval === v} data-testid={`interval-${v}-btn`} onClick={() => setInterval(v)} className={`what-mode-btn ${interval === v ? "is-on" : ""}`}>
                {label}
                {v === "year" && plans && <span data-testid="yearly-saving" className="text-[11px] font-mono opacity-80">save {Math.round((1 - plans.pro.yearly_price / (plans.pro.price * 12)) * 100)}%</span>}
              </button>
            ))}
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-3 mt-2">
          <div data-testid="plan-card-free" className={`what-plan ${!isPro ? "is-current" : ""}`}>
            <p className="font-mono text-xs uppercase tracking-[0.16em] text-slate-500">Free</p>
            <p className="font-display text-3xl font-bold text-white mt-2">{freePrice}</p>
            <Perks items={FREE} accent="#64748B" />
          </div>
          <div data-testid="plan-card-pro" className={`what-plan what-plan-pro ${isPro ? "is-current" : ""}`}>
            <p className="font-mono text-xs uppercase tracking-[0.16em] text-[#D4FF3A] flex items-center gap-1.5"><Sparkles size={12} /> Pro</p>
            <p className="font-display text-3xl font-bold text-white mt-2" data-testid="pro-price">
              {price}<span className="text-sm font-normal text-slate-500"> / {per}</span>
            </p>
            {yearly && plans && <p data-testid="yearly-per-month" className="text-xs text-slate-500 mt-1">About {fmt(Math.round(plans.pro.yearly_price / 12))} a month</p>}
            <Perks items={PRO} accent="#D4FF3A" />
          </div>
        </div>
        {isPro ? (
          <button data-testid="cancel-plan-btn" onClick={cancel} disabled={busy} className="what-btn-ghost w-full mt-2">
            {busy && <Loader2 size={15} className="animate-spin" />} {onTrial ? "Cancel free trial" : "Cancel Pro subscription"}
          </button>
        ) : (
          <>
            <ProviderPicker providers={plans?.providers} value={provider} onChange={setProvider} />
            <button data-testid="upgrade-checkout-btn" onClick={upgrade} disabled={busy || !plans || !providerReady} className="what-btn-primary w-full !py-3">
              {busy && <Loader2 size={15} className="animate-spin" />}
              {trialEligible ? "Start 14-day free trial" : `Upgrade to Pro — ${price}/${per}`}
            </button>
          </>
        )}
        {trialEligible && (
          <p data-testid="trial-terms" className="text-xs text-slate-400 text-center">
            Card required.{" "}
            {provider === "payfast"
              ? "Nothing is charged today."
              : `We make a ${symbol}1 card check and refund it straight away.`}{" "}
            After 14 days you'll be billed {price}/{per} unless you cancel.
          </p>
        )}
        {!isPro && plans && !providerReady && (
          <p data-testid="payments-not-configured" className="text-xs text-amber-400 text-center">
            {provider === "payfast" ? "PayFast" : "Paystack"} payments aren't set up yet. Please check back soon.
          </p>
        )}
        <p className="text-xs text-slate-500 text-center">Secure payment by {provider === "payfast" ? "PayFast" : "Paystack"}. Cancel anytime.</p>
        {user?.role === "admin" && !isPro && plans?.providers?.payfast && (
          <div data-testid="live-test-box" className="rounded-xl border border-amber-400/30 bg-amber-400/5 p-4 text-xs text-amber-200">
            <p className="font-semibold">Admin: PayFast live check</p>
            <p className="mt-1 text-amber-200/80">Pay a one-off R5 with your own card to confirm real payments work. It switches Pro on for this account. Refund it afterwards in your PayFast dashboard and cancel Pro here.</p>
            <button data-testid="live-test-payment-btn" onClick={liveTest} disabled={busy} className="what-btn-ghost mt-3">Pay R5 test with PayFast</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
